import { useRef, useCallback, useEffect, useState } from 'react'
import { TIMELINE_DURATION_MINUTES, TIMELINE_START_HOUR, getDateIsoFromXPercent } from '../../../shared/lib/timeline'
import { DefaultDurations } from '../../../shared/lib/blockRanges'
import { ResolvedBreak } from '../../../entities/time-entry/model/useBreakBlock'
import { Appointment } from '@myapp/contract'

const BREAK_DURATION = 30

type HasCollisionFn = (
  iso: string,
  dur: DefaultDurations,
  techId: string,
  excludeId?: string,
  excludeTaskId?: number,
  excludeBreakForTechnicianId?: string,
) => boolean

interface UseBreakInteractionOptions {
  technicians?: { technicianId: string; appointments: Appointment[]; workingDays: number[]; shiftStart: string | null; shiftEnd: string | null }[]
  breakBlocks: Map<string, ResolvedBreak>
  dayIso: string
  onBreakMoved: (technicianId: string, newStartTimeIso: string) => void
  hasCollision?: HasCollisionFn
}

interface DragState {
  technicianId: string
  trackEl: HTMLElement
  offsetMinutes: number
}

export function useBreakInteraction({
  technicians,
  breakBlocks,
  dayIso,
  onBreakMoved,
  hasCollision,
}: UseBreakInteractionOptions) {
  const dragRef = useRef<DragState | null>(null)
  const lastDragPosRef = useRef<{ technicianId: string; startTimeIso: string } | null>(null)
  const [draggingBreak, setDraggingBreak] = useState<{ technicianId: string; startTimeIso: string } | null>(null)

  const isBreakWithinShift = useCallback(
    (technicianId: string, breakStartIso: string) => {
      const tech = technicians?.find((t) => t.technicianId === technicianId)
      if (!tech) return true
      const dow = new Date(dayIso).getDay()
      const mapped = dow === 0 ? 7 : dow
      if (!tech.workingDays.includes(mapped)) return false
      if (!tech.shiftStart || !tech.shiftEnd) return true
      const shiftStart = new Date(tech.shiftStart)
      const shiftEnd = new Date(tech.shiftEnd)
      const shiftStartH = shiftStart.getUTCHours() + shiftStart.getUTCMinutes() / 60
      const shiftEndH = shiftEnd.getUTCHours() + shiftEnd.getUTCMinutes() / 60
      const breakDate = new Date(breakStartIso)
      const breakStartH = breakDate.getHours() + breakDate.getMinutes() / 60
      const breakEndH = breakStartH + BREAK_DURATION / 60
      return breakStartH >= shiftStartH && breakEndH <= shiftEndH
    },
    [technicians, dayIso],
  )

  const getBreakDragStart = useCallback(
    (technicianId: string) => (e: React.MouseEvent) => {
      const trackEl = (e.target as HTMLElement).closest('[data-timeline-track]') as HTMLElement
      if (!trackEl) return
      e.preventDefault()

      const breakBlock = breakBlocks.get(technicianId)
      if (!breakBlock) return

      const rect = trackEl.getBoundingClientRect()
      const cursorPercent = ((e.clientX - rect.left) / rect.width) * 100
      const cursorMinutes = (cursorPercent / 100) * TIMELINE_DURATION_MINUTES

      const breakDate = new Date(breakBlock.startTimeIso)
      const breakMinutes = breakDate.getHours() * 60 + breakDate.getMinutes() - TIMELINE_START_HOUR * 60
      const offsetMinutes = cursorMinutes - breakMinutes

      dragRef.current = { technicianId, trackEl, offsetMinutes }
      setDraggingBreak({ technicianId, startTimeIso: breakBlock.startTimeIso })
    },
    [breakBlocks],
  )

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const drag = dragRef.current
      if (!drag) return

      const rect = drag.trackEl.getBoundingClientRect()
      const cursorPercent = ((e.clientX - rect.left) / rect.width) * 100
      const blockStartPercent = cursorPercent - (drag.offsetMinutes / TIMELINE_DURATION_MINUTES) * 100
      const newIso = getDateIsoFromXPercent(blockStartPercent, dayIso)

      const breakDurations: DefaultDurations = { plannedTravelDuration: 0, plannedWorkDuration: BREAK_DURATION, plannedDepartureDuration: 0 }
      const collides = hasCollision
        ? hasCollision(newIso, breakDurations, drag.technicianId, undefined, undefined, drag.technicianId)
        : false

      if (!collides && isBreakWithinShift(drag.technicianId, newIso)) {
        const pos = { technicianId: drag.technicianId, startTimeIso: newIso }
        lastDragPosRef.current = pos
        setDraggingBreak(pos)
      }
    }

    const handleMouseUp = () => {
      const drag = dragRef.current
      if (!drag) return
      dragRef.current = null

      const pos = lastDragPosRef.current
      lastDragPosRef.current = null
      setDraggingBreak(null)

      if (pos) {
        onBreakMoved(pos.technicianId, pos.startTimeIso)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dayIso, hasCollision, isBreakWithinShift, onBreakMoved])

  const getResolvedBreak = useCallback(
    (technicianId: string): ResolvedBreak | null => {
      if (draggingBreak?.technicianId === technicianId) {
        return {
          startTimeIso: draggingBreak.startTimeIso,
          overrideId: breakBlocks.get(technicianId)?.overrideId ?? null,
        }
      }
      return breakBlocks.get(technicianId) ?? null
    },
    [draggingBreak, breakBlocks],
  )

  return {
    getBreakDragStart,
    getResolvedBreak,
    draggingBreakInfo: draggingBreak,
  }
}
